import type { TabGroupWC, TabWC } from '@pypestream/design-system';

import type { ChannelSettingsTab } from './channel-settings-tabs';

/** What the pass did, so callers and tests can assert it rather than infer it. */
export type SelectInitialTabResult =
  | 'absent'
  | 'already-active'
  | 'claimed'
  | 'switched'
  | 'failed';

/**
 * Read `panel` / `active` as PROPERTIES, never attributes.
 *
 * `@lit/react` hands JSX props to the element as properties, and `ps-tab`
 * declares both as reflecting Lit properties — reflection runs on Lit's async
 * update cycle, so at the moment this is called (a layout effect, same task as
 * the render) `getAttribute('panel')` is still empty. Matching on the attribute
 * therefore never finds the tab, and the group silently falls back to its first
 * one. That is the whole bug this file exists to prevent, so it must not be
 * reintroduced by "simplifying" these back to attributes.
 */
const panelOf = (tab: Element): string =>
  (tab as TabWC).panel ?? tab.getAttribute('panel') ?? '';
const isActive = (tab: Element): boolean =>
  (tab as TabWC).active ?? tab.hasAttribute('active');

/**
 * Point the tab group at `desiredPanel`.
 *
 * Must be called again whenever the set of tabs changes, not just on mount: the
 * Pype Next Theme tab only renders once LaunchDarkly answers, which is normally
 * AFTER first paint. A mount-only pass runs while that tab is still absent, the
 * group falls back to `tabs[0]` ("Settings"), and when the flag lands the new
 * tab appears beside an already-active Settings.
 */
export const selectInitialTab = (
  // `show` is optional because this runs from a layout effect, where the custom
  // element may not have upgraded yet.
  group: Element & { show?: TabGroupWC['show'] },
  desiredPanel: ChannelSettingsTab
): SelectInitialTabResult => {
  const tabs = Array.from(group.querySelectorAll('ps-tab'));
  const target = tabs.find((tab) => panelOf(tab) === desiredPanel);

  if (!target) {
    return 'absent';
  }

  if (isActive(target)) {
    return 'already-active';
  }

  // An active tab anywhere means the group has already made its choice and owns
  // `activeTab` — moving `active` behind its back would leave the panels
  // pointing at the old tab, so ask it to switch.
  if (tabs.some(isActive)) {
    // `show()` resolves against the group's OWN tab cache, which is refreshed
    // only on `slotchange` / `updateComplete` — both later than this layout
    // effect. So a tab appended in the same task is not in that cache yet and
    // `show()` silently returns. Report what actually happened instead of
    // assuming it worked; `failed` is the caller's cue to retry once the group
    // has caught up.
    group.show?.(desiredPanel);
    return isActive(target) ? 'switched' : 'failed';
  }

  // Nothing chosen yet. Claim the tab so the group's own
  // `getActiveTab() ?? tabs[0]` pass — which it runs once it first becomes
  // visible — finds this tab and agrees with it.
  tabs.forEach((tab) => {
    (tab as TabWC).active = tab === target;
  });

  return 'claimed';
};
